import {
  loadOnboardingResult,
  loadOnboardingSession,
  normalizeOptionalText,
} from "./onboarding";
import { getTimelineLabel } from "./onboarding-types";
import type {
  LenderExecution,
  PricingMode,
  RateExplanation,
} from "./agent-types";
import type {
  AffordabilityScenario,
  BackendEngineRates,
  BootstrapStatus,
  FinancialAssumptionMeta,
  FinancialFreshnessState,
  FinancialsRateMarketHistoryPoint,
  FinancialsRateWatchCurrentQuote,
  FinancialsRateWatchFeeLine,
  FinancialsRatePersonalizedHistoryPoint,
  FinancialsRateWatchPricingLine,
  FinancialsRateWatchComparisonProduct,
  FinancialInputMeta,
  FinancialsRateWatch,
  FinancialVerificationTier,
  LoanProduct,
} from "./onboarding-types";

export type DashboardView =
  | "overview"
  | "passport"
  | "readiness"
  | "properties"
  | "documents"
  | "rate-watch"
  | "feed"
  | "advisor";
export type DashboardPrimaryView =
  | "overview"
  | "passport"
  | "readiness"
  | "properties"
  | "documents"
  | "rate-watch"
  | "feed"
  | "advisor";
export type AdvisorPanel = "profile" | "chat";
export type AdvisorContext =
  | "score"
  | "milestones"
  | "rates"
  | "share"
  | "property"
  | "cash_to_close"
  | null;

export const ADVISOR_PROMPTS = {
  cashToClose:
    "Run the cash-to-close calculator for my current profile and target, then explain the biggest cash drivers, what looks uncertain, and the practical ways I could reduce cash needed.",
  financeInputs:
    "Review my finance inputs and tell me what is missing or most likely to change my options.",
  profileReview:
    "Review my profile and tell me which inputs are strongest, weakest, and what I should verify next.",
  referralBenefits:
    "Explain how referral benefits unlock and what I should tell a friend.",
  scoreImprovement: "What would move my score the most from here?",
  trueCostPlan:
    "Review my true cost plan and tell me what cash, monthly payment, and levers I should focus on next.",
} as const;

export interface TrackedProperty {
  id: string;
  address: string;
  cityMeta: string;
  listPrice: number;
  monthlyPayment: number | null;
  readinessLabel?: string | null;
  readinessScore?: number | null;
  visualToken?: string | null;
}

export type FinanceReadinessPreviewTier =
  | "self-reported"
  | "document-backed"
  | "bank-verified"
  | "lender-approved";

export type FinanceReadinessPreviewCategory =
  | "cash-to-close"
  | "income"
  | "monthly-comfort"
  | "debts"
  | "shopping-target"
  | "credit-band";

export interface FinanceReadinessPreviewItem {
  action: "verify" | "refresh" | null;
  category: FinanceReadinessPreviewCategory;
  freshnessState?: FinancialFreshnessState;
  id: string;
  isInferred: boolean;
  label: string;
  subcopy?: string;
  tier: FinanceReadinessPreviewTier;
  updatedAt: Date | null;
  value: string;
  verifiedAt: Date | null;
}

// DashboardFeedItem RETIRED (Plan 287 M3): static placeholder feed replaced
// by the real notification-backed /feed route. Removed fields: id, eyebrow,
// title, detail, emphasis.

export interface RateWatchSnapshot {
  assumptions: FinancialAssumptionMeta[];
  automationCadence: string;
  automationSummary: string;
  baseRateAsOfLabel: string | null;
  baseRatePercent: number | null;
  comparisonProducts?: DashboardRateWatchComparisonProduct[];
  feeLines?: DashboardRateWatchFeeLine[];
  history: DashboardRateWatchHistoryPoint[];
  locationCode?: string | null;
  llpaPoints: number | null;
  missingInputs: string[];
  nextAutomationWindow: string | null;
  personalizedRatePercent: number | null;
  previousPersonalizedRatePercent: number | null;
  lenderExecutions?: LenderExecution[];
  pricingMode?: PricingMode | null;
  pricingModeNote?: string | null;
  pricingLines?: DashboardRateWatchPricingLine[];
  rateExplanation?: RateExplanation | null;
  scenarioPayment: number | null;
  status: BootstrapStatus | "preview";
  targetPrice?: number | null;
  weeklyDeltaPercent: number | null;
}

export interface DashboardRateWatchHistoryPoint {
  baseRatePercent: number | null;
  createdAt: string;
  dateLabel: string;
  estimatedPaymentMonthly: number | null;
  llpaPoints: number | null;
  marketObservationDate: string | null;
  personalizedRatePercent: number | null;
  pricingSourceAsOf: string | null;
  pricingSourceName: string | null;
  source: "stored" | "live" | "market" | "preview" | "synthetic";
  trigger:
    | "initial"
    | "profile_update"
    | "target_update"
    | "rate_change"
    | "llpa_change";
}

export interface DashboardRateWatchBadge {
  slug: "primary" | "ready" | "derived" | "preview";
  label: string;
  tone: "default" | "warning" | "accent";
  tooltip?: string;
}

export interface DashboardRateWatchComparisonProduct {
  badge: DashboardRateWatchBadge;
  primaryOverlay: boolean;
  baseRatePercent: number | null;
  estimatedPaymentMonthly: number | null;
  feeLines?: DashboardRateWatchFeeLine[];
  history: DashboardRateWatchHistoryPoint[];
  id: string;
  label: string;
  llpaPoints: number | null;
  locationCode?: string | null;
  loanTermMonths: number;
  personalizedRatePercent: number | null;
  lenderExecutions?: LenderExecution[];
  pricingMode?: PricingMode | null;
  pricingModeNote?: string | null;
  pricingLines?: DashboardRateWatchPricingLine[];
  pricingSourceAsOf: string | null;
  pricingSourceName: string | null;
  primary: boolean;
  product: LoanProduct;
  rateExplanation?: RateExplanation | null;
  shortLabel: string;
  status: BootstrapStatus | "preview";
  targetPrice?: number | null;
}

export interface DashboardRateWatchPricingLine {
  kind: string;
  points: number | null;
  rate: number | null;
  source: string | null;
  sourceAsOf: string | null;
  sourceLabel: string | null;
}

export interface DashboardRateWatchFeeLine {
  amount: number;
  annualRate: number | null;
  financed: boolean;
  kind: string;
  source: string | null;
  sourceAsOf: string | null;
  sourceLabel: string | null;
  sourceUrl: string | null;
}

type PreviewRateWatchComparisonSeed = {
  feeLines?: ReadonlyArray<DashboardRateWatchFeeLine>;
  id: string;
  label: string;
  llpaOffset: number;
  loanTermMonths: number;
  paymentOffset: number;
  primary: boolean;
  product: LoanProduct;
  rateOffset: number;
  shortLabel: string;
};

const previewRateWatchHistorySeed = [
  {
    baseRatePercent: 0.0685,
    createdAt: "2026-04-05T08:00:00Z",
    estimatedPaymentMonthly: 3395,
    llpaPoints: 0.01,
    marketObservationDate: "2026-04-01T00:00:00Z",
    personalizedRatePercent: 0.071,
    pricingSourceAsOf: "2026-04-01T00:00:00Z",
    pricingSourceName: "fred-mortgage30us",
    trigger: "initial" as const,
  },
  {
    baseRatePercent: 0.06775,
    createdAt: "2026-04-12T08:00:00Z",
    estimatedPaymentMonthly: 3325,
    llpaPoints: 0.01,
    marketObservationDate: "2026-04-08T00:00:00Z",
    personalizedRatePercent: 0.07025,
    pricingSourceAsOf: "2026-04-08T00:00:00Z",
    pricingSourceName: "fred-mortgage30us",
    trigger: "rate_change" as const,
  },
  {
    baseRatePercent: 0.0675,
    createdAt: "2026-04-19T08:00:00Z",
    estimatedPaymentMonthly: 3260,
    llpaPoints: 0.00875,
    marketObservationDate: "2026-04-15T00:00:00Z",
    personalizedRatePercent: 0.0695,
    pricingSourceAsOf: "2026-04-15T00:00:00Z",
    pricingSourceName: "fred-mortgage30us",
    trigger: "llpa_change" as const,
  },
];

const previewRateWatchComparisonSeed: ReadonlyArray<PreviewRateWatchComparisonSeed> =
  [
    {
      id: "conventional_30",
      label: "30-yr fixed",
      llpaOffset: 0,
      loanTermMonths: 360,
      paymentOffset: 0,
      primary: true,
      product: "conventional",
      rateOffset: 0,
      shortLabel: "30 yr",
    },
    {
      id: "conventional_15",
      label: "15-yr fixed",
      llpaOffset: -0.00125,
      loanTermMonths: 180,
      paymentOffset: 465,
      primary: false,
      product: "conventional",
      rateOffset: -0.008,
      shortLabel: "15 yr",
    },
    {
      id: "arm_7_6",
      label: "7/6 ARM",
      llpaOffset: 0,
      loanTermMonths: 360,
      paymentOffset: -160,
      primary: false,
      product: "arm_7_6",
      rateOffset: -0.007,
      shortLabel: "7/6 ARM",
    },
    {
      feeLines: [
        {
          amount: 22500,
          annualRate: 0.0055,
          financed: false,
          kind: "fha_annual_mip",
          source: "product_policy_fee_schedule",
          sourceAsOf: null,
          sourceLabel: "fha",
          sourceUrl: null,
        },
        {
          amount: 849000,
          annualRate: 0.0175,
          financed: true,
          kind: "fha_upfront_mip",
          source: "product_policy_fee_schedule",
          sourceAsOf: null,
          sourceLabel: "fha",
          sourceUrl: null,
        },
      ],
      id: "fha_30",
      label: "FHA 30-yr",
      llpaOffset: -0.00875,
      loanTermMonths: 360,
      paymentOffset: -95,
      primary: false,
      product: "fha",
      rateOffset: -0.0015,
      shortLabel: "FHA",
    },
    {
      feeLines: [
        {
          amount: 1043000,
          annualRate: 0.0215,
          financed: true,
          kind: "va_funding_fee",
          source: "product_policy_fee_schedule",
          sourceAsOf: null,
          sourceLabel: "va",
          sourceUrl: null,
        },
      ],
      id: "va_30",
      label: "VA 30-yr",
      llpaOffset: -0.00875,
      loanTermMonths: 360,
      paymentOffset: -135,
      primary: false,
      product: "va",
      rateOffset: -0.0025,
      shortLabel: "VA",
    },
    {
      feeLines: [
        {
          amount: 14200,
          annualRate: 0.0035,
          financed: false,
          kind: "usda_annual_fee",
          source: "product_policy_fee_schedule",
          sourceAsOf: null,
          sourceLabel: "usda",
          sourceUrl: null,
        },
        {
          amount: 485000,
          annualRate: 0.01,
          financed: true,
          kind: "usda_guarantee_fee",
          source: "product_policy_fee_schedule",
          sourceAsOf: null,
          sourceLabel: "usda",
          sourceUrl: null,
        },
      ],
      id: "usda_30",
      label: "USDA 30-yr",
      llpaOffset: -0.00875,
      loanTermMonths: 360,
      paymentOffset: -70,
      primary: false,
      product: "usda",
      rateOffset: -0.001,
      shortLabel: "USDA",
    },
    {
      id: "jumbo_30",
      label: "Jumbo 30-yr",
      llpaOffset: -0.00875,
      loanTermMonths: 360,
      paymentOffset: 180,
      primary: false,
      product: "jumbo",
      rateOffset: 0.0025,
      shortLabel: "Jumbo",
    },
  ];

export interface DashboardAffordabilityScenario {
  assumptions: FinancialAssumptionMeta[];
  confidence: string | null;
  customerSummary: string | null;
  downPaymentNeeded: number;
  downPaymentPercent: number;
  hasPMI: boolean;
  label: string;
  maxDTIRatio: number;
  maxPurchasePrice: number;
  missingInputs: string[];
  monthlyPayment: number;
  status: BootstrapStatus | "preview";
}

export interface EngineRates {
  baseRatePercent: number | null;
  effectivePropertyTaxRatePercent: number | null;
  effectiveInsuranceRatePercent: number | null;
  monthlyMortgageInsuranceCents: number | null;
}

export interface DashboardSnapshot {
  agentConnected: boolean;
  trackedProperties: TrackedProperty[];
  advisorName: string;
  affordabilityScenarios: DashboardAffordabilityScenario[];
  engineRates: EngineRates | null;
  automationsSummary: string;
  buyerType: string;
  creditBucket: string | null;
  educationProgress: number;
  educationStep: string;
  email: string | null;
  employmentMonths?: number | null;
  financialInputs: FinancialInputMeta[];
  financeReadinessPreview?: FinanceReadinessPreviewItem[];
  financialsStatus: BootstrapStatus | "preview" | "recomputing";
  firstName: string;
  hasBonusIncome?: boolean;
  hasCommissionIncome?: boolean;
  // SILL-173 journey-rail predicate inputs sourced from MeResponse.
  // hasConversation drives ask-sill; hasUploadedDocument || hasPlaidItem
  // drives verify-finances; publishedAt drives publish-profile.
  hasConversation: boolean;
  hasCreditScore: boolean;
  hasDpaCheck: boolean;
  hasEducationStarted: boolean;
  hasPlaidItem: boolean;
  hasSecondJob?: boolean;
  hasUploadedDocument: boolean;
  housingSituation: string;
  householdIncome: number;
  isFirstTimeBuyer: boolean;
  isSelfEmployed?: boolean;
  monthlyBudget: number;
  monthlyDebts: number;
  // SILL-537 M5 (vf-shape-editable): the profile's stored income_shape,
  // mirrored verbatim from the wire (`profile.income_shape`) — never a
  // client-side fabricated default. Null/undefined means "no stored
  // value on this read"; the income-shape row renders that as an
  // explicit "Not set" state rather than guessing a shape.
  incomeShape?: string | null;
  reserveFloorMonths?: number;
  comfortDtiPct?: number | null;
  monthlyPaymentMax: number;
  profileId: string | null;
  // Current snapshot id (per ADR-024). Required for PATCH /api/profile's
  // optimistic-concurrency guard. Optional on the type so test fixtures
  // can omit; callers gate their edit hooks on it being a non-empty string.
  currentSnapshotId?: string | null;
  profileCompleteness: number;
  // SILL-173: user-facing labels for fields not yet filled. Drives the
  // "Still to add" list inside the rail's profile-completeness widget
  // and gates the "Continue onboarding" CTA's visibility (empty list
  // means the profile is complete). Optional so test fixtures and
  // preview pages can omit; consumers default to an empty list.
  profileCompletenessMissing?: readonly string[];
  // SILL-173 journey-rail predicate input. ISO-8601 string when the
  // profile has EVER been published; RETAINED after unpublish (audit
  // timestamp). Sourced from ProfileResult.published_at on the MeResponse.
  // Use this for the "first went live" date, NOT for "currently live".
  publishedAt: string | null;
  // Whether the passport is CURRENTLY live/discoverable to agents. This is the
  // truth for the live/not-live state (published_at persists after unpublish,
  // so it can't answer "am I live now?"). Sourced from
  // ProfileResult.portal_discoverable on the MeResponse. Optional so snapshot
  // literals in tests/preview omit it; absent defaults to not-live (fail-safe).
  portalDiscoverable?: boolean;
  rateWatch: RateWatchSnapshot;
  rateSummary: string;
  rateWatchDelta: string;
  readinessDescription: string;
  readinessInsight: string;
  readinessLabel: string;
  readinessStatus: BootstrapStatus | "preview";
  // Nullable so a capped engine result (SILL-318 PlanUnderfunded, SILL-323
  // DNQ; see ADR-025) flows through composition without being silently
  // collapsed to a stale v1 score. Callers must render the missing-state
  // placeholder when null.
  readinessScore: number | null;
  recentJobGap?: boolean;
  refreshedAt: string | null;
  savings: number;
  targetArea: string;
  targetPrice: number;
  timeline: string;
  timelineToken: string | null;
  leaseExpiration?: string | null;
  leaseMonthsRemaining?: number | null;
  userId: string | null;
  buyingPowerMax: number;
  // percent is a whole-number (e.g. 9 for 9%), not a decimal.
  downPayment: { percent: number; amount: number } | null;
  // feed field RETIRED (Plan 287 M3): static placeholder removed;
  // real notifications are fetched by FeedPage from /api/consumer/notifications.
  /**
   * Affordability overlay outcome fields (M1 / Buyer Passport hero).
   * Sourced from the backend's projection when available; absent/null
   * until the consumer endpoint exposes them or the agent-preview
   * endpoint (M2) is fetched. When absent, the hero renders the
   * estimate-only fallback from buyingPowerMax.
   *
   * bandLow/bandHigh: comfortable→stretch price band in dollars.
   * sillQualPrice: Sill qualification price in dollars.
   */
  affordabilityBandLow?: number | null;
  affordabilityBandHigh?: number | null;
  sillQualPrice?: number | null;
}

const PRIMARY_VIEWS: DashboardPrimaryView[] = [
  "overview",
  "passport",
  "readiness",
  "properties",
  "documents",
  "rate-watch",
  "feed",
  "advisor",
];

export function formatCompactCurrency(value: number) {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `$${Math.round(value / 1_000)}K`;
  }

  return `$${value.toLocaleString()}`;
}

const MIN_REASONABLE_MAX_PAYMENT_TO_PRICE_RATIO = 0.0035;
const FALLBACK_MAX_PAYMENT_TO_PRICE_RATIO = 0.006;
const AFFORDABILITY_SCENARIO_MATCH_TOLERANCE = 0.12;

export function reconcileMaxPurchaseMonthlyPayment({
  affordabilityScenarios = [],
  buyingPowerMax,
  monthlyPaymentMax,
}: {
  affordabilityScenarios?: DashboardAffordabilityScenario[];
  buyingPowerMax: number;
  monthlyPaymentMax: number;
}) {
  if (buyingPowerMax <= 0) {
    return monthlyPaymentMax;
  }

  if (isReasonableMaxPurchasePayment(buyingPowerMax, monthlyPaymentMax)) {
    return monthlyPaymentMax;
  }

  const scenarioPayment = monthlyPaymentFromMatchingScenario(
    affordabilityScenarios,
    buyingPowerMax,
  );
  if (scenarioPayment !== null) {
    return scenarioPayment;
  }

  return Math.round(buyingPowerMax * FALLBACK_MAX_PAYMENT_TO_PRICE_RATIO);
}

export function resolveDashboardView(
  searchParams: URLSearchParams,
): DashboardView {
  const view = searchParams.get("view");

  // ?view=passport is canonical. Legacy ?view=profile and ?view=financials
  // links still resolve to the same surface so existing bookmarks keep working.
  if (view === "passport" || view === "profile" || view === "financials") {
    return "passport";
  }

  if (
    view === "readiness" ||
    view === "properties" ||
    view === "documents" ||
    view === "rate-watch" ||
    view === "feed" ||
    view === "advisor"
  ) {
    return view;
  }

  return "overview";
}

function isReasonableMaxPurchasePayment(price: number, payment: number) {
  return payment >= price * MIN_REASONABLE_MAX_PAYMENT_TO_PRICE_RATIO;
}

function monthlyPaymentFromMatchingScenario(
  scenarios: DashboardAffordabilityScenario[],
  buyingPowerMax: number,
) {
  if (buyingPowerMax <= 0 || scenarios.length === 0) {
    return null;
  }

  const matched = [...scenarios]
    .filter(
      (scenario) =>
        scenario.maxPurchasePrice > 0 && scenario.monthlyPayment > 0,
    )
    .sort(
      (left, right) =>
        Math.abs(left.maxPurchasePrice - buyingPowerMax) -
        Math.abs(right.maxPurchasePrice - buyingPowerMax),
    )[0];
  if (!matched) {
    return null;
  }

  const relativeDelta =
    Math.abs(matched.maxPurchasePrice - buyingPowerMax) / buyingPowerMax;
  return relativeDelta <= AFFORDABILITY_SCENARIO_MATCH_TOLERANCE
    ? matched.monthlyPayment
    : null;
}

export function resolveAdvisorPanel(
  searchParams: URLSearchParams,
): AdvisorPanel {
  return searchParams.get("panel") === "profile" ? "profile" : "chat";
}

export function resolveAdvisorContext(
  searchParams: URLSearchParams,
): AdvisorContext {
  const context = searchParams.get("context");

  if (
    context === "score" ||
    context === "milestones" ||
    context === "rates" ||
    context === "share" ||
    context === "property" ||
    context === "cash_to_close"
  ) {
    return context;
  }

  return null;
}

export function buildDashboardSearchParams(
  currentSearchParams: URLSearchParams,
  updates: {
    context?: AdvisorContext | undefined;
    newChat?: boolean | undefined;
    panel?: AdvisorPanel | undefined;
    prompt?: string | null | undefined;
    submitPrompt?: boolean | undefined;
    view?: DashboardView | undefined;
  },
) {
  const next = new URLSearchParams(currentSearchParams);
  const nextView = updates.view ?? resolveDashboardView(currentSearchParams);

  next.delete("tab");
  next.delete("section");

  if (nextView === "overview") {
    next.delete("view");
  } else {
    next.set("view", nextView);
  }

  if (nextView === "advisor") {
    const panel = updates.panel ?? resolveAdvisorPanel(currentSearchParams);
    next.set("panel", panel);

    const context =
      updates.context ?? resolveAdvisorContext(currentSearchParams);

    if (context) {
      next.set("context", context);
    } else {
      next.delete("context");
    }

    if ("prompt" in updates) {
      const prompt = updates.prompt?.trim();
      if (prompt) {
        next.set("prompt", prompt);
      } else {
        next.delete("prompt");
      }
    }

    if (updates.newChat) {
      next.set("new_chat", "1");
    } else if ("newChat" in updates) {
      next.delete("new_chat");
    }

    if (updates.submitPrompt) {
      next.set("send", "1");
    } else if ("submitPrompt" in updates) {
      next.delete("send");
    }
  } else {
    next.delete("panel");
    next.delete("context");
    next.delete("prompt");
    next.delete("new_chat");
    next.delete("send");
  }

  return next;
}

export function isPrimaryDashboardView(
  view: DashboardView,
): view is DashboardPrimaryView {
  return PRIMARY_VIEWS.includes(view as DashboardPrimaryView);
}

export function getAdvisorContextCopy(
  context: AdvisorContext,
  snapshot: DashboardSnapshot,
) {
  if (context === "score") {
    return {
      title: "Score coaching focus",
      description:
        snapshot.readinessScore !== null
          ? `Coach me from ${snapshot.readinessScore} to the next readiness tier with the fastest moves this month.`
          : "Coach me to the next readiness tier with the fastest moves this month.",
    };
  }

  if (context === "milestones") {
    return {
      title: "Milestone planning focus",
      description:
        "Map my next milestone and tell me which action will unlock the biggest progress.",
    };
  }

  if (context === "rates") {
    return {
      title: "Rate watch focus",
      description:
        "Explain what current rate movement means for my payment range and when to check again.",
    };
  }

  if (context === "share") {
    return {
      title: "Referral benefit focus",
      description:
        "Help me share Sill with a friend and explain what unlocks when they finish their profile.",
    };
  }

  if (context === "property") {
    return {
      title: "Property planning focus",
      description:
        "Review this saved property's fit, scenarios, assumptions, and next action before you decide what to do with it.",
    };
  }

  if (context === "cash_to_close") {
    return {
      title: "Cash-to-close focus",
      description:
        "Explain upfront cash, closing costs, prepaids, savings gap, and which levers could reduce cash needed.",
    };
  }

  return {
    title: "Ask Sill",
    description:
      "Ask Sill is your AI advisor for next steps, option comparisons, and the numbers behind your readiness score.",
  };
}

// SILL-173: 13 onboarding fields (7 required + 6 optional). Mirrors
// backend onboarding.computeCompleteness *minus* `agent_id` — the rail's
// "connect-agent" step is its own disabled milestone (step 6, "Coming
// soon"), so listing it as a missing onboarding field would tell the
// user to fix something via /onboarding that onboarding can't fix.
// Drift would only cause cosmetic mismatch on the rail's "Still to add"
// list; the widget's "Profile complete" affordance overrides the
// backend's percent column for users whose only missing item is agent.
const PROFILE_COMPLETENESS_FIELDS: ReadonlyArray<{
  key: string;
  label: string;
  isFilled: (result: NonNullable<ReturnType<typeof loadOnboardingResult>>) => boolean;
}> = [
  // Re-based to the five financial-profile rows the user actually sees and
  // can fill, matching the backend's liveProfileCompleteness (readiness
  // engine). Intake plumbing (name, buyer type, target area/price, monthly
  // budget, timeline, housing, lease end) and the unsupported Personal & ID
  // row are intentionally excluded — they were unreachable or invisible, so
  // the bar could never legitimately reach 100%. Existing debts of 0 is a
  // valid answer ("no debts"), so it counts as filled by presence, not >0;
  // income and cash of 0 read as unanswered.
  { key: "household_income", label: "Income", isFilled: (r) => (r.profile.household_income ?? 0) > 0 },
  { key: "employment_months", label: "Employment", isFilled: (r) => r.profile.employment_months != null },
  { key: "savings", label: "Cash & assets", isFilled: (r) => (r.profile.savings ?? 0) > 0 },
  { key: "monthly_debts", label: "Existing debts", isFilled: (r) => r.profile.monthly_debts != null },
  { key: "credit_score", label: "Credit", isFilled: (r) => Boolean(r.profile.credit_score) },
];

export function buildMissingProfileCompletenessFields(
  result: ReturnType<typeof loadOnboardingResult>,
): readonly string[] {
  if (!result) return [];
  return PROFILE_COMPLETENESS_FIELDS.filter((f) => !f.isFilled(result)).map(
    (f) => f.label,
  );
}

export function buildDashboardSnapshot(): DashboardSnapshot {
  const result = loadOnboardingResult();
  const session = loadOnboardingSession();
  const sessionData = session?.data;
  const profile = result?.profile ?? null;
  const hasBackendProfile = profile !== null;
  const usePreviewFallbacks = !hasBackendProfile;
  const resolveProfileBackedValue = <T>(
    profileValue: T | null | undefined,
    sessionValue: T | null | undefined,
    fallback: T,
  ) =>
    hasBackendProfile ? (profileValue ?? fallback) : (sessionValue ?? fallback);
  const targetPrice = resolveProfileBackedValue(
    profile?.target_price,
    sessionData?.budget,
    usePreviewFallbacks ? 485_000 : 0,
  );
  const householdIncome = resolveProfileBackedValue(
    profile?.household_income,
    sessionData?.householdIncome,
    usePreviewFallbacks ? Math.round(targetPrice * 0.26) : 0,
  );
  const savings = resolveProfileBackedValue(
    profile?.savings,
    sessionData?.savings,
    usePreviewFallbacks ? Math.round(targetPrice * 0.09) : 0,
  );
  const monthlyBudget = resolveProfileBackedValue(
    profile?.monthly_budget,
    sessionData?.monthlyBudget,
    usePreviewFallbacks ? Math.max(1_900, Math.round(targetPrice * 0.0058)) : 0,
  );
  const reserveFloorMonths = resolveProfileBackedValue(
    profile?.reserve_floor_months,
    undefined,
    3,
  );
  const comfortDtiPct = resolveProfileBackedValue<number | null>(
    profile?.comfort_dti_pct ?? null,
    undefined,
    null,
  );
  const monthlyDebts = resolveProfileBackedValue(
    profile?.monthly_debts,
    sessionData?.monthlyDebts,
    0,
  );
  const buyerTypeCode = resolveProfileBackedValue(
    profile?.buyer_type,
    sessionData?.buyerType,
    usePreviewFallbacks ? "first-time" : "",
  );
  const creditScoreCode = resolveProfileBackedValue<string | null>(
    profile?.credit_score ?? null,
    sessionData?.creditScore ?? null,
    null,
  );
  // SILL-537 M5: no session-only equivalent (the field lands after the
  // backend profile exists) — reads through unchanged whenever a backend
  // profile is present, else stays null (never a fabricated default).
  const incomeShape = resolveProfileBackedValue<string | null>(
    profile?.income_shape ?? null,
    undefined,
    null,
  );
  const creditBucket =
    profile?.credit_bucket ?? toEstimateCreditBucket(creditScoreCode);
  const readinessStatus = result?.readiness
    ? (result.readiness.status ?? "ready")
    : usePreviewFallbacks
      ? "preview"
      : "unavailable";
  const financialsStatus = result?.financials
    ? (result.financials.status ??
      (result.financials.rate_watch ||
      (result.financials.affordability_scenarios?.length ?? 0) > 0
        ? "ready"
        : "unavailable"))
    : usePreviewFallbacks
      ? "preview"
      : "unavailable";
  const financialInputs = result?.financials?.inputs ?? [];

  const readinessScore =
    result?.readiness.score ?? (usePreviewFallbacks ? 72 : 0);
  const readinessLabel =
    result?.readiness.label ??
    (usePreviewFallbacks ? "Very Good" : "Needs inputs");
  const readinessDescription =
    result?.readiness.description ??
    (usePreviewFallbacks
      ? "You have a strong base. A few focused steps can move you from exploration into confident buying mode."
      : "Complete your profile inputs to unlock a live readiness snapshot.");
  const targetArea = resolveProfileBackedValue(
    profile?.target_area,
    sessionData?.targetArea,
    usePreviewFallbacks ? "Austin, TX" : "",
  );
  const timelineTokenRaw = resolveProfileBackedValue(
    profile?.timeline,
    sessionData?.timeline,
    usePreviewFallbacks ? "3-6months" : "",
  );
  const timelineToken = timelineTokenRaw === "" ? null : timelineTokenRaw;
  const timeline = getTimelineLabel(timelineTokenRaw) ?? "";
  const housingSituation = humanizeSlug(
    resolveProfileBackedValue(
      profile?.housing_situation,
      sessionData?.housingSituation,
      usePreviewFallbacks ? "renting" : "",
    ),
  );
  const leaseExpiration =
    profile?.lease_expiration ?? sessionData?.leaseExpiration ?? null;
  const leaseMonthsRemaining =
    housingSituation.toLowerCase() === "renting"
      ? getMonthsUntilDate(leaseExpiration)
      : null;
  const employmentMonths = resolveProfileBackedValue<number | null>(
    profile?.employment_months ?? null,
    sessionData?.employmentMonths ?? null,
    usePreviewFallbacks ? 24 : null,
  );
  const recentJobGap = resolveProfileBackedValue(
    profile?.recent_job_gap,
    sessionData?.recentJobGap,
    false,
  );
  const hasSecondJob = resolveProfileBackedValue(
    profile?.has_second_job,
    sessionData?.hasSecondJob,
    false,
  );
  const isSelfEmployed = resolveProfileBackedValue(
    profile?.is_self_employed,
    sessionData?.isSelfEmployed,
    false,
  );
  const hasBonusIncome = resolveProfileBackedValue(
    profile?.has_bonus_income,
    sessionData?.hasBonusIncome,
    false,
  );
  const hasCommissionIncome = resolveProfileBackedValue(
    profile?.has_commission_income,
    sessionData?.hasCommissionIncome,
    false,
  );
  const profileCompleteness =
    result?.profile.profile_completeness ?? (usePreviewFallbacks ? 78 : 0);
  const buyingPowerMax =
    result?.readiness.buying_power_max ??
    (usePreviewFallbacks ? Math.round(targetPrice * 1.08) : 0);
  const affordabilityScenarios = buildAffordabilityScenarios(
    result?.financials?.affordability_scenarios ?? null,
  );
  const engineRates = buildEngineRates(
    result?.financials?.engine_rates ?? null,
  );
  const rawMonthlyPaymentMax =
    result?.readiness.monthly_payment_max ??
    (usePreviewFallbacks ? Math.round(monthlyBudget * 1.14) : 0);
  const monthlyPaymentMax = reconcileMaxPurchaseMonthlyPayment({
    affordabilityScenarios,
    buyingPowerMax,
    monthlyPaymentMax: rawMonthlyPaymentMax,
  });
  const readinessInsight =
    result?.readiness.insight ??
    (usePreviewFallbacks
      ? "Most of your gap is documentation and timing, not affordability. Keep momentum and the score should rise quickly."
      : "Once your live snapshot is ready, Sill will explain what is helping and what still needs confirmation.");
  const firstName =
    normalizeOptionalText(result?.user.first_name) ??
    normalizeOptionalText(sessionData?.firstName) ??
    "You";
  const email = result?.user.email ?? sessionData?.email ?? null;
  const connectedAgent = result?.connected_pros?.find(
    (pro) => pro.connected && pro.role === "agent",
  );
  const advisorName =
    (hasBackendProfile
      ? (connectedAgent?.name ?? result?.agent?.name)
      : sessionData?.agentName) ?? "Mina Park";
  const buyerType = humanizeSlug(buyerTypeCode);
  const hasCreditScore = Boolean(creditScoreCode);
  const hasDpaCheck = Boolean(buyerTypeCode && targetArea && householdIncome);
  const hasEducationStarted = profileCompleteness >= 60;
  const agentConnected = hasBackendProfile
    ? Boolean(connectedAgent || result?.agent?.connected)
    : Boolean(sessionData?.agentName);
  const rateWatch = buildRateWatchSnapshot({
    allowPreviewFallback: usePreviewFallbacks,
    backendRateWatch: result?.financials?.rate_watch ?? null,
    creditBucket,
    isFirstTimeBuyer: buyerTypeCode === "first-time",
    monthlyPaymentMax,
  });
  const financeReadinessPreview = buildFinanceReadinessPreview({
    creditBucket,
    financialInputs,
    householdIncome,
    monthlyBudget,
    monthlyDebts,
    savings,
    targetArea,
    targetPrice,
    usePreviewFallbacks,
  });

  const downPayment: DashboardSnapshot["downPayment"] =
    targetPrice > 0
      ? {
          percent: Math.round((savings / targetPrice) * 100),
          amount: savings,
        }
      : null;

  const refreshedAt =
    result?.financials?.as_of ?? result?.readiness?.as_of ?? null;

  return {
    agentConnected,
    advisorName,
    affordabilityScenarios,
    engineRates,
    trackedProperties: usePreviewFallbacks
      ? [
          {
            id: "preview-a",
            address: "1428 Humboldt St",
            cityMeta: "Denver, CO · 3bd / 2ba",
            listPrice: 525000,
            monthlyPayment: 3180,
          },
          {
            id: "preview-b",
            address: "4210 Fox Run Way",
            cityMeta: "Lakewood, CO · 4bd / 2ba",
            listPrice: 489000,
            monthlyPayment: 2970,
          },
        ]
      : [],
    automationsSummary:
      "2 smart check-ins active: weekly rate watch and milestone nudges.",
    buyingPowerMax,
    buyerType,
    creditBucket,
    educationProgress: Math.min(
      92,
      Math.max(18, Math.round(profileCompleteness * 0.72)),
    ),
    educationStep: "Complete your 12-minute homebuyer education primer.",
    email,
    employmentMonths,
    financialInputs,
    financeReadinessPreview,
    financialsStatus,
    // feed RETIRED (Plan 287 M3): buildFeed removed; FeedPage fetches real data.
    firstName,
    hasBonusIncome,
    hasCommissionIncome,
    // SILL-173: backend-sourced booleans (default false until backend
    // wires the corresponding MeResponse fields in Phase 3).
    hasConversation: Boolean(result?.user?.has_conversation),
    hasCreditScore,
    hasDpaCheck,
    hasEducationStarted,
    hasPlaidItem: Boolean(profile?.has_plaid_item),
    hasSecondJob,
    hasUploadedDocument: Boolean(profile?.has_uploaded_document),
    housingSituation,
    householdIncome,
    isFirstTimeBuyer: buyerTypeCode === "first-time",
    isSelfEmployed,
    monthlyBudget,
    monthlyDebts,
    incomeShape,
    reserveFloorMonths,
    comfortDtiPct,
    monthlyPaymentMax,
    profileId: result?.profile.id ?? null,
    currentSnapshotId: result?.profile.current_snapshot_id ?? null,
    profileCompleteness,
    profileCompletenessMissing: buildMissingProfileCompletenessFields(result),
    publishedAt: result?.profile.published_at ?? null,
    portalDiscoverable: result?.profile.portal_discoverable ?? false,
    rateSummary: buildRateSummary(rateWatch),
    rateWatch,
    rateWatchDelta: formatSignedPercentage(resolveRateWatchTrend(rateWatch), 3),
    readinessDescription,
    readinessInsight,
    readinessLabel,
    readinessStatus,
    readinessScore,
    recentJobGap,
    refreshedAt,
    savings,
    targetArea,
    targetPrice,
    timeline,
    timelineToken,
    leaseExpiration,
    leaseMonthsRemaining,
    userId: result?.user.id ?? null,
    downPayment,
    // Buyer Passport M2 task 677: affordability overlay outcome sourced from
    // the backend's onboarding response (backend wires these from the frozen
    // readiness snapshot on POST /api/onboarding). Absent/null until the
    // backend exposes them; when absent the hero renders the estimate-only
    // fallback from buyingPowerMax. The agent-preview endpoint (M3 hook) is
    // the second source — hero and AgentPreviewCard share the SAME overlay
    // source, not the same instant ("same source, not same instant" invariant).
    affordabilityBandLow:
      typeof profile?.affordability_band_low === "number"
        ? profile.affordability_band_low
        : null,
    affordabilityBandHigh:
      typeof profile?.affordability_band_high === "number"
        ? profile.affordability_band_high
        : null,
    sillQualPrice:
      typeof profile?.sill_qual_price === "number"
        ? profile.sill_qual_price
        : null,
  };
}

// buildFeed RETIRED (Plan 287 M3): static placeholder feed replaced by the
// real notification-backed /feed route (FeedPage + notifications-api.ts).
// The emphasis field was set but never rendered — also retired.

function getMonthsUntilDate(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  const target = new Date(`${value}T00:00:00`);
  if (Number.isNaN(target.getTime())) {
    return null;
  }
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );
  if (end <= start) {
    return null;
  }
  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (end.getDate() > start.getDate()) {
    months += 1;
  }
  return Math.max(1, months);
}

// formatMonthCount (Plan 287 M3): was used by buildFeed, which is retired.
// Removed alongside buildFeed.

type FinanceReadinessValueArgs = {
  creditBucket: string | null;
  householdIncome: number;
  monthlyBudget: number;
  monthlyDebts: number;
  savings: number;
  targetArea: string;
  targetPrice: number;
};

type FinanceReadinessConfig = {
  category: FinanceReadinessPreviewCategory;
  fields: string[];
  id: string;
  label: string;
  value: (args: FinanceReadinessValueArgs) => string | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const FINANCE_READINESS_CONFIG: readonly FinanceReadinessConfig[] = [
  {
    category: "income",
    fields: ["household_income"],
    id: "household_income",
    label: "Income",
    value: ({ householdIncome }) =>
      householdIncome > 0
        ? `${formatCompactCurrency(householdIncome)}/yr`
        : null,
  },
  {
    category: "cash-to-close",
    fields: ["savings"],
    id: "savings",
    label: "Cash to close",
    value: ({ savings }) =>
      savings > 0 ? `${formatCompactCurrency(savings)} saved` : null,
  },
  {
    category: "monthly-comfort",
    fields: ["monthly_budget"],
    id: "monthly_budget",
    label: "Monthly comfort",
    value: ({ monthlyBudget }) =>
      monthlyBudget > 0 ? `${formatCompactCurrency(monthlyBudget)}/mo` : null,
  },
  {
    category: "debts",
    fields: ["monthly_debts"],
    id: "monthly_debts",
    label: "Existing debts",
    value: ({ monthlyDebts }) =>
      monthlyDebts > 0
        ? `-${formatCompactCurrency(monthlyDebts)}/mo`
        : monthlyDebts === 0
          ? "Clear"
          : null,
  },
  {
    category: "shopping-target",
    fields: ["target_price", "target_area"],
    id: "target_price",
    label: "Shopping target",
    value: ({ targetArea, targetPrice }) => {
      if (targetPrice > 0 && targetArea.length > 0) {
        return `${formatCompactCurrency(targetPrice)} in ${targetArea}`;
      }
      if (targetPrice > 0) {
        return formatCompactCurrency(targetPrice);
      }
      return targetArea.length > 0 ? targetArea : null;
    },
  },
  {
    category: "credit-band",
    fields: ["credit_score"],
    id: "credit_score",
    label: "Credit band",
    value: ({ creditBucket }) =>
      creditBucket ? getCreditBandLabel(creditBucket) : null,
  },
] as const;

function buildFinanceReadinessPreview({
  creditBucket,
  financialInputs,
  householdIncome,
  monthlyBudget,
  monthlyDebts,
  savings,
  targetArea,
  targetPrice,
  usePreviewFallbacks,
}: FinanceReadinessValueArgs & {
  financialInputs: FinancialInputMeta[];
  usePreviewFallbacks: boolean;
}): FinanceReadinessPreviewItem[] {
  const args = {
    creditBucket,
    householdIncome,
    monthlyBudget,
    monthlyDebts,
    savings,
    targetArea,
    targetPrice,
  };

  const directItems = FINANCE_READINESS_CONFIG.flatMap((config) => {
    const input = financialInputs.find(
      (item) => item.present && config.fields.includes(item.field),
    );
    const value = config.value(args);

    if (!input || value === null) {
      return [];
    }

    return [
      buildFinanceReadinessItem({
        config,
        input,
        value,
      }),
    ];
  });

  if (directItems.length > 0) {
    return directItems
      .sort((left, right) => compareFinanceReadinessItems(left, right))
      .slice(0, 4);
  }

  const fallbackItems = FINANCE_READINESS_CONFIG.flatMap((config, index) => {
    const value = config.value(args);

    if (value === null) {
      return [];
    }

    return [
      buildFallbackFinanceReadinessItem({
        config,
        updatedAt:
          usePreviewFallbacks || hasMeaningfulFinancialSignals(args)
            ? new Date(Date.now() - Math.max(1, index) * DAY_MS)
            : null,
        value,
      }),
    ];
  });

  if (!usePreviewFallbacks && !hasMeaningfulFinancialSignals(args)) {
    return [];
  }

  return fallbackItems.slice(0, 4);
}

function hasMeaningfulFinancialSignals({
  creditBucket,
  householdIncome,
  monthlyBudget,
  monthlyDebts,
  savings,
  targetArea,
  targetPrice,
}: FinanceReadinessValueArgs) {
  return (
    creditBucket !== null ||
    householdIncome > 0 ||
    monthlyBudget > 0 ||
    monthlyDebts > 0 ||
    savings > 0 ||
    targetArea.length > 0 ||
    targetPrice > 0
  );
}

function buildFinanceReadinessItem({
  config,
  input,
  value,
}: {
  config: FinanceReadinessConfig;
  input: FinancialInputMeta;
  value: string;
}): FinanceReadinessPreviewItem {
  const credit = config.category === "credit-band";
  const tier = credit ? "self-reported" : mapFinanceReadinessTier(input.verification_tier);
  const freshnessState = input.freshness_state;
  const verifiedAt = credit ? null : parseOptionalDate(input.last_verified_at);
  const updatedAt = verifiedAt ?? parseOptionalDate(input.source_as_of);
  const isInferred =
    input.derivation_kind === "inferred" || input.source === "inferred";

  return {
    action: credit ? null : resolveFinanceReadinessAction({
      freshnessState,
      isInferred,
      tier,
    }),
    category: config.category,
    freshnessState,
    id: config.id,
    isInferred,
    label: config.label,
    subcopy: buildFinanceReadinessSubcopy({
      category: config.category,
      freshnessState,
      isInferred,
      tier,
    }),
    tier,
    updatedAt,
    value,
    verifiedAt,
  };
}

function buildFallbackFinanceReadinessItem({
  config,
  updatedAt,
  value,
}: {
  config: FinanceReadinessConfig;
  updatedAt: Date | null;
  value: string;
}): FinanceReadinessPreviewItem {
  return {
    action: config.category === "credit-band" ? null : "verify",
    category: config.category,
    id: config.id,
    isInferred: false,
    label: config.label,
    subcopy: buildFinanceReadinessSubcopy({
      category: config.category,
      freshnessState: undefined,
      isInferred: false,
      tier: "self-reported",
    }),
    tier: "self-reported",
    updatedAt,
    value,
    verifiedAt: null,
  };
}

function resolveFinanceReadinessAction({
  freshnessState,
  isInferred,
  tier,
}: {
  freshnessState?: FinancialFreshnessState;
  isInferred: boolean;
  tier: FinanceReadinessPreviewTier;
}) {
  if (isInferred) {
    return null;
  }

  if (tier === "self-reported") {
    return "verify";
  }

  if (
    tier === "document-backed" &&
    freshnessState &&
    freshnessState !== "fresh"
  ) {
    return "refresh";
  }

  return null;
}

function buildFinanceReadinessSubcopy({
  category,
  freshnessState,
  isInferred,
  tier,
}: {
  category: FinanceReadinessPreviewCategory;
  freshnessState?: FinancialFreshnessState;
  isInferred: boolean;
  tier: FinanceReadinessPreviewTier;
}) {
  if (isInferred) {
    return "Inferred from the rest of your profile.";
  }

  if (category === "cash-to-close" && tier === "self-reported") {
    return "verify to lock this in";
  }

  if (tier === "document-backed" && freshnessState === "aging") {
    return "Refresh these documents before they go stale.";
  }

  if (tier === "document-backed" && freshnessState === "stale") {
    return "Refresh these documents to restore full strength.";
  }

  return undefined;
}

function compareFinanceReadinessItems(
  left: FinanceReadinessPreviewItem,
  right: FinanceReadinessPreviewItem,
) {
  const priorityDelta =
    financeReadinessPriority(right) - financeReadinessPriority(left);

  if (priorityDelta !== 0) {
    return priorityDelta;
  }

  const leftIndex = FINANCE_READINESS_CONFIG.findIndex(
    (config) => config.id === left.id,
  );
  const rightIndex = FINANCE_READINESS_CONFIG.findIndex(
    (config) => config.id === right.id,
  );

  return leftIndex - rightIndex;
}

function financeReadinessPriority(item: FinanceReadinessPreviewItem) {
  if (item.tier === "lender-approved") {
    return 60;
  }

  if (item.tier === "bank-verified") {
    return 50;
  }

  if (item.tier === "document-backed") {
    if (item.freshnessState === "fresh") return 40;
    if (item.freshnessState === "aging") return 30;
    if (item.freshnessState === "stale") return 20;
    return 35;
  }

  if (item.isInferred) {
    return 5;
  }

  return 10;
}

export function mapFinanceReadinessTier(
  tier: FinancialVerificationTier | undefined,
): FinanceReadinessPreviewTier {
  switch (tier) {
    case "document":
      return "document-backed";
    case "connected":
      return "bank-verified";
    case "pre_approval":
      return "lender-approved";
    case "self_reported":
    default:
      return "self-reported";
  }
}

const TIER_RANK: Record<FinanceReadinessPreviewTier, number> = {
  "self-reported": 0,
  "document-backed": 1,
  "bank-verified": 2,
  "lender-approved": 3,
};

const TIER_BY_RANK: FinanceReadinessPreviewTier[] = [
  "self-reported",
  "document-backed",
  "bank-verified",
  "lender-approved",
];

export function deriveHighestVerificationTier(
  // Only financialInputs is read — the narrow Pick lets surfaces with a
  // reduced snapshot prop (e.g. OverviewScorePanel) derive their tier too.
  snapshot: Pick<DashboardSnapshot, "financialInputs">,
): FinanceReadinessPreviewTier {
  const inputs = snapshot.financialInputs ?? [];
  let best = 0;
  for (const input of inputs) {
    if (!input.present) continue;
    const rank = TIER_RANK[mapFinanceReadinessTier(input.verification_tier)];
    if (rank > best) best = rank;
  }
  return TIER_BY_RANK[best];
}

function parseOptionalDate(value: string | undefined) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function humanizeSlug(value: string) {
  return value.replaceAll("-", " ");
}

function getCreditBandLabel(creditBucket: string) {
  switch (creditBucket) {
    case "excellent_740+":
      return "Excellent (740+)";
    case "good_700_739":
      return "Good (700-739)";
    case "fair_660_699":
      return "Fair (660-699)";
    case "below_660":
      return "Below 660";
    default:
      return "Credit band still being inferred";
  }
}

function toEstimateCreditBucket(value: string | null | undefined) {
  switch (value) {
    case "excellent":
      return "excellent_740+";
    case "good":
      return "good_700_739";
    case "fair":
      return "fair_660_699";
    case "below":
    case "unsure":
      return "below_660";
    default:
      return null;
  }
}

function buildRateWatchSnapshot({
  allowPreviewFallback,
  backendRateWatch,
  creditBucket,
  isFirstTimeBuyer,
  monthlyPaymentMax,
}: {
  allowPreviewFallback: boolean;
  backendRateWatch: FinancialsRateWatch | null;
  creditBucket: string | null;
  isFirstTimeBuyer: boolean;
  monthlyPaymentMax: number;
}): RateWatchSnapshot {
  const currentQuote = backendRateWatch?.current_quote ?? null;
  const marketHistory = buildMarketRateWatchHistory(
    backendRateWatch?.market_history,
  );
  const personalizedHistory = buildStoredRateWatchHistory(
    backendRateWatch?.personalized_history,
  );
  const livePoint = buildLiveRateWatchHistoryPoint(currentQuote);
  const history = buildRateWatchTimeline({
    allowPreviewFallback,
    allowSyntheticVisualization: false,
    livePoint,
    marketHistory,
    personalizedHistory,
  });
  const latestHistoryPoint = history.at(-1) ?? null;
  const latestMarketPoint = marketHistory.at(-1) ?? null;
  const previousMarketPoint =
    marketHistory.length > 1 ? marketHistory[marketHistory.length - 2] : null;
  const previousPersonalizedPoint = resolvePreviousPersonalizedPoint(
    personalizedHistory,
    livePoint,
  );
  const fallbackBaseRatePercent =
    currentQuote?.base_rate_percent ??
    latestHistoryPoint?.baseRatePercent ??
    (allowPreviewFallback ? 0.06625 : null);
  const fallbackWeeklyDeltaPercent =
    latestMarketPoint &&
    previousMarketPoint &&
    typeof latestMarketPoint.baseRatePercent === "number" &&
    typeof previousMarketPoint.baseRatePercent === "number"
      ? latestMarketPoint.baseRatePercent - previousMarketPoint.baseRatePercent
      : allowPreviewFallback
        ? -0.00125
        : null;
  const fallbackLLPAPoints =
    currentQuote?.llpa_points ??
    latestHistoryPoint?.llpaPoints ??
    (allowPreviewFallback
      ? Math.max(
          0.00125,
          baseLLPAPointsForCreditBucket(creditBucket) -
            (isFirstTimeBuyer ? 0.00125 : 0),
        )
      : null);
  const fallbackScenarioPayment =
    currentQuote?.estimated_payment_monthly ??
    latestHistoryPoint?.estimatedPaymentMonthly ??
    (allowPreviewFallback ? monthlyPaymentMax : null);
  const fallbackPersonalizedRatePercent =
    currentQuote?.personalized_rate_percent ??
    latestHistoryPoint?.personalizedRatePercent ??
    (allowPreviewFallback
      ? (previewRateWatchHistorySeed.at(-1)?.personalizedRatePercent ?? null)
      : null);
  const status =
    currentQuote?.status ??
    (currentQuote &&
    (typeof currentQuote.base_rate_percent === "number" ||
      typeof currentQuote.personalized_rate_percent === "number" ||
      typeof currentQuote.llpa_points === "number")
      ? "ready"
      : allowPreviewFallback
        ? "preview"
        : "unavailable");
  const comparisonProducts = buildRateWatchComparisonProducts({
    allowPreviewFallback,
    backendComparisonProducts: backendRateWatch?.comparison_products,
    fallbackBaseRatePercent,
    fallbackEstimatedPaymentMonthly: fallbackScenarioPayment,
    fallbackLLPAPoints,
    fallbackPersonalizedRatePercent,
    history,
    primaryPricingSourceAsOf:
      currentQuote?.pricing_source_as_of ??
      currentQuote?.base_rate_as_of ??
      latestHistoryPoint?.pricingSourceAsOf ??
      latestHistoryPoint?.marketObservationDate ??
      null,
    primaryPricingSourceName:
      currentQuote?.pricing_source_name ??
      latestHistoryPoint?.pricingSourceName ??
      null,
    status,
  });

  return {
    assumptions: currentQuote?.assumptions ?? [],
    automationCadence: "Weekly automation",
    automationSummary:
      "Sill sends one calm summary when the base rate or your payment scenario moves enough to matter.",
    baseRateAsOfLabel: formatMonthDayLabel(
      currentQuote?.base_rate_as_of ??
        currentQuote?.pricing_source_as_of ??
        latestMarketPoint?.marketObservationDate ??
        latestHistoryPoint?.marketObservationDate ??
        latestHistoryPoint?.pricingSourceAsOf ??
        null,
      allowPreviewFallback,
    ),
    baseRatePercent: fallbackBaseRatePercent,
    comparisonProducts,
    feeLines: mapRateWatchFeeLines(currentQuote?.fee_lines),
    history,
    locationCode: currentQuote?.location_code ?? null,
    llpaPoints: fallbackLLPAPoints,
    missingInputs: currentQuote?.missing_inputs ?? [],
    nextAutomationWindow: "Monday morning",
    personalizedRatePercent: fallbackPersonalizedRatePercent,
    previousPersonalizedRatePercent:
      previousPersonalizedPoint?.personalizedRatePercent ??
      (allowPreviewFallback
        ? (previewRateWatchHistorySeed.at(-2)?.personalizedRatePercent ?? null)
        : null),
    lenderExecutions: currentQuote?.lender_executions ?? [],
    pricingMode: currentQuote?.pricing_mode ?? null,
    pricingModeNote: currentQuote?.pricing_mode_note ?? null,
    pricingLines: mapRateWatchPricingLines(currentQuote?.pricing_lines),
    rateExplanation: currentQuote?.rate_explanation ?? null,
    scenarioPayment: fallbackScenarioPayment,
    status,
    targetPrice: currentQuote?.target_price ?? null,
    weeklyDeltaPercent: fallbackWeeklyDeltaPercent,
  };
}

function buildRateWatchComparisonProducts({
  allowPreviewFallback,
  backendComparisonProducts,
  fallbackBaseRatePercent,
  fallbackEstimatedPaymentMonthly,
  fallbackLLPAPoints,
  fallbackPersonalizedRatePercent,
  history,
  primaryPricingSourceAsOf,
  primaryPricingSourceName,
  status,
}: {
  allowPreviewFallback: boolean;
  backendComparisonProducts: FinancialsRateWatchComparisonProduct[] | undefined;
  fallbackBaseRatePercent: number | null;
  fallbackEstimatedPaymentMonthly: number | null;
  fallbackLLPAPoints: number | null;
  fallbackPersonalizedRatePercent: number | null;
  history: DashboardRateWatchHistoryPoint[];
  primaryPricingSourceAsOf: string | null;
  primaryPricingSourceName: string | null;
  status: BootstrapStatus | "preview" | "unavailable";
}): DashboardRateWatchComparisonProduct[] {
  if (backendComparisonProducts?.length) {
    return backendComparisonProducts.map((product) =>
      buildDashboardRateWatchComparisonProduct({
        primaryHistory: history,
        product,
      }),
    );
  }

  if (allowPreviewFallback) {
    return buildPreviewRateWatchComparisonProducts(history);
  }

  if (
    history.length === 0 &&
    typeof fallbackBaseRatePercent !== "number" &&
    typeof fallbackPersonalizedRatePercent !== "number" &&
    typeof fallbackLLPAPoints !== "number" &&
    typeof fallbackEstimatedPaymentMonthly !== "number"
  ) {
    return [];
  }

  return [
    {
      badge: { slug: "ready", label: "Ready", tone: "default" },
      primaryOverlay: true,
      baseRatePercent: fallbackBaseRatePercent,
      estimatedPaymentMonthly: fallbackEstimatedPaymentMonthly,
      feeLines: [],
      history,
      id: "conventional_30",
      label: "30-yr fixed",
      locationCode: null,
      llpaPoints: fallbackLLPAPoints,
      loanTermMonths: 360,
      personalizedRatePercent: fallbackPersonalizedRatePercent,
      lenderExecutions: [],
      pricingMode: null,
      pricingModeNote: null,
      pricingLines: [],
      pricingSourceAsOf: primaryPricingSourceAsOf,
      pricingSourceName: primaryPricingSourceName,
      primary: true,
      product: "conventional",
      rateExplanation: null,
      shortLabel: "30 yr",
      status,
      targetPrice: null,
    },
  ];
}

function buildDashboardRateWatchComparisonProduct({
  primaryHistory,
  product,
}: {
  primaryHistory: DashboardRateWatchHistoryPoint[];
  product: FinancialsRateWatchComparisonProduct;
}): DashboardRateWatchComparisonProduct {
  const marketHistory = buildMarketRateWatchHistory(product.market_history);
  const productHistory =
    product.primary && primaryHistory.length > 0
      ? primaryHistory
      : buildRateWatchTimeline({
          allowPreviewFallback: false,
          allowSyntheticVisualization: false,
          livePoint: buildLiveRateWatchHistoryPointFromValues({
            baseRatePercent: product.base_rate_percent ?? null,
            createdAt: product.pricing_source_as_of ?? null,
            estimatedPaymentMonthly: product.estimated_payment_monthly ?? null,
            llpaPoints: product.llpa_points ?? null,
            marketObservationDate: null,
            personalizedRatePercent: product.personalized_rate_percent ?? null,
            pricingSourceAsOf: product.pricing_source_as_of ?? null,
            pricingSourceName: product.pricing_source_name ?? null,
          }),
          marketHistory,
          personalizedHistory: [],
        });

  return {
    badge: product.badge ?? {
      slug: "ready",
      label: "Ready",
      tone: "default",
    },
    primaryOverlay: Boolean(product.primary_overlay),
    baseRatePercent: product.base_rate_percent ?? null,
    estimatedPaymentMonthly: product.estimated_payment_monthly ?? null,
    feeLines: mapRateWatchFeeLines(product.fee_lines),
    history: productHistory,
    id: product.id,
    label: product.label,
    locationCode: product.location_code ?? null,
    llpaPoints: product.llpa_points ?? null,
    loanTermMonths: product.loan_term_months,
    personalizedRatePercent: product.personalized_rate_percent ?? null,
    lenderExecutions: product.lender_executions ?? [],
    pricingMode: product.pricing_mode ?? null,
    pricingModeNote: product.pricing_mode_note ?? null,
    pricingLines: mapRateWatchPricingLines(product.pricing_lines),
    pricingSourceAsOf: product.pricing_source_as_of ?? null,
    pricingSourceName: product.pricing_source_name ?? null,
    primary: Boolean(product.primary),
    product: product.product,
    rateExplanation: product.rate_explanation ?? null,
    shortLabel: product.short_label,
    status: product.status,
    targetPrice: product.target_price ?? null,
  };
}

function buildPreviewRateWatchComparisonProducts(
  history: DashboardRateWatchHistoryPoint[],
): DashboardRateWatchComparisonProduct[] {
  const latest = previewRateWatchHistorySeed.at(-1);
  if (!latest) {
    return [];
  }

  return previewRateWatchComparisonSeed.map((product) => {
    const baseRatePercent =
      typeof latest.baseRatePercent === "number"
        ? latest.baseRatePercent + product.rateOffset
        : null;
    const personalizedRatePercent =
      typeof latest.personalizedRatePercent === "number"
        ? latest.personalizedRatePercent + product.rateOffset
        : null;
    const llpaPoints =
      typeof latest.llpaPoints === "number"
        ? Math.max(0, latest.llpaPoints + product.llpaOffset)
        : null;
    const estimatedPaymentMonthly =
      typeof latest.estimatedPaymentMonthly === "number"
        ? latest.estimatedPaymentMonthly + product.paymentOffset
        : null;

    const productHistory =
      product.primary && history.length > 0
        ? history
        : buildRateWatchTimeline({
            allowPreviewFallback: true,
            allowSyntheticVisualization: true,
            livePoint: buildLiveRateWatchHistoryPointFromValues({
              baseRatePercent,
              createdAt: latest.createdAt,
              estimatedPaymentMonthly,
              llpaPoints,
              marketObservationDate: latest.marketObservationDate,
              personalizedRatePercent,
              pricingSourceAsOf: latest.pricingSourceAsOf,
              pricingSourceName: latest.pricingSourceName,
            }),
            marketHistory: [],
            personalizedHistory: [],
          });

    return {
      badge: { slug: "preview", label: "Preview", tone: "default" },
      primaryOverlay: product.primary,
      baseRatePercent,
      estimatedPaymentMonthly,
      feeLines: product.feeLines?.map((line) => ({ ...line })) ?? [],
      history: productHistory,
      id: product.id,
      label: product.label,
      locationCode: null,
      llpaPoints,
      loanTermMonths: product.loanTermMonths,
      personalizedRatePercent,
      pricingLines: [],
      pricingSourceAsOf: latest.pricingSourceAsOf,
      pricingSourceName: latest.pricingSourceName,
      primary: product.primary,
      product: product.product,
      shortLabel: product.shortLabel,
      status: "preview",
      targetPrice: null,
    };
  });
}

function mapRateWatchPricingLines(
  lines: FinancialsRateWatchPricingLine[] | undefined,
): DashboardRateWatchPricingLine[] {
  if (!lines?.length) {
    return [];
  }
  return lines.map((line) => ({
    kind: line.kind,
    points: line.points ?? null,
    rate: line.rate ?? null,
    source: line.source ?? null,
    sourceAsOf: line.source_as_of ?? null,
    sourceLabel: line.source_label ?? null,
  }));
}

function mapRateWatchFeeLines(
  lines: FinancialsRateWatchFeeLine[] | undefined,
): DashboardRateWatchFeeLine[] {
  if (!lines?.length) {
    return [];
  }
  return lines.map((line) => ({
    amount: line.amount,
    annualRate: line.annual_rate ?? null,
    financed: line.financed,
    kind: line.kind,
    source: line.source ?? null,
    sourceAsOf: line.source_as_of ?? null,
    sourceLabel: line.source_label ?? null,
    sourceUrl: line.source_url ?? null,
  }));
}

function buildRateWatchTimeline({
  allowPreviewFallback,
  allowSyntheticVisualization,
  livePoint,
  marketHistory,
  personalizedHistory,
}: {
  allowPreviewFallback: boolean;
  allowSyntheticVisualization: boolean;
  livePoint: DashboardRateWatchHistoryPoint | null;
  marketHistory: DashboardRateWatchHistoryPoint[];
  personalizedHistory: DashboardRateWatchHistoryPoint[];
}): DashboardRateWatchHistoryPoint[] {
  if (marketHistory.length > 0 || personalizedHistory.length > 0) {
    const merged = mergeRateWatchTimeline({
      livePoint,
      marketHistory,
      personalizedHistory,
    });
    if (merged.length > 0) {
      return merged;
    }
  }

  if (livePoint) {
    if (allowSyntheticVisualization) {
      return buildLiveRateWatchVisualizationHistory(livePoint);
    }
    return [livePoint];
  }

  return allowPreviewFallback ? buildPreviewRateWatchHistory() : [];
}

function buildStoredRateWatchHistory(
  history: FinancialsRatePersonalizedHistoryPoint[] | undefined,
): DashboardRateWatchHistoryPoint[] {
  if (!history?.length) {
    return [];
  }

  return history.map((point) => ({
    baseRatePercent: point.base_rate_percent ?? null,
    createdAt: point.created_at,
    dateLabel: formatMonthDayLabel(point.created_at, false) ?? "Saved point",
    estimatedPaymentMonthly: point.estimated_payment_monthly ?? null,
    llpaPoints: point.llpa_points ?? null,
    marketObservationDate: point.market_observation_date ?? null,
    personalizedRatePercent: point.personalized_rate_percent ?? null,
    pricingSourceAsOf: point.pricing_source_as_of ?? null,
    pricingSourceName: point.pricing_source_name ?? null,
    source: "stored",
    trigger: point.trigger,
  }));
}

function buildMarketRateWatchHistory(
  history: FinancialsRateMarketHistoryPoint[] | undefined,
): DashboardRateWatchHistoryPoint[] {
  if (!history?.length) {
    return [];
  }

  return history.map((point) => ({
    baseRatePercent: point.base_rate_percent ?? null,
    createdAt: point.observation_date,
    dateLabel:
      formatMonthDayLabel(point.observation_date, false) ?? "Market point",
    estimatedPaymentMonthly: null,
    llpaPoints: null,
    marketObservationDate: point.observation_date,
    personalizedRatePercent: null,
    pricingSourceAsOf: point.observation_date,
    pricingSourceName: point.pricing_source_name ?? null,
    source: "market",
    trigger: "rate_change",
  }));
}

function mergeRateWatchTimeline({
  livePoint,
  marketHistory,
  personalizedHistory,
}: {
  livePoint: DashboardRateWatchHistoryPoint | null;
  marketHistory: DashboardRateWatchHistoryPoint[];
  personalizedHistory: DashboardRateWatchHistoryPoint[];
}): DashboardRateWatchHistoryPoint[] {
  const pointsByDay = new Map<string, DashboardRateWatchHistoryPoint>();

  const upsertPoint = (
    key: string,
    mutate: (
      point: DashboardRateWatchHistoryPoint,
    ) => DashboardRateWatchHistoryPoint,
    fallback: DashboardRateWatchHistoryPoint,
  ) => {
    const current = pointsByDay.get(key) ?? fallback;
    pointsByDay.set(key, mutate(current));
  };

  for (const point of marketHistory) {
    const key = rateWatchDateKey(
      point.marketObservationDate ?? point.createdAt,
    );
    upsertPoint(
      key,
      (current) => ({
        ...current,
        baseRatePercent: point.baseRatePercent ?? current.baseRatePercent,
        createdAt:
          current.source === "live" ? current.createdAt : point.createdAt,
        dateLabel: point.dateLabel,
        marketObservationDate:
          point.marketObservationDate ?? current.marketObservationDate,
        pricingSourceAsOf: point.pricingSourceAsOf ?? current.pricingSourceAsOf,
        pricingSourceName: point.pricingSourceName ?? current.pricingSourceName,
        source: current.source === "live" ? current.source : "market",
      }),
      point,
    );
  }

  for (const point of personalizedHistory) {
    const key = rateWatchDateKey(point.createdAt);
    upsertPoint(
      key,
      (current) => ({
        ...current,
        baseRatePercent: current.baseRatePercent ?? point.baseRatePercent,
        createdAt: point.createdAt,
        dateLabel: point.dateLabel,
        estimatedPaymentMonthly: point.estimatedPaymentMonthly,
        llpaPoints: point.llpaPoints,
        marketObservationDate:
          point.marketObservationDate ?? current.marketObservationDate,
        personalizedRatePercent: point.personalizedRatePercent,
        pricingSourceAsOf: point.pricingSourceAsOf ?? current.pricingSourceAsOf,
        pricingSourceName: point.pricingSourceName ?? current.pricingSourceName,
        source: point.source,
        trigger: point.trigger,
      }),
      point,
    );
  }

  if (livePoint) {
    const key = rateWatchDateKey(livePoint.createdAt);
    upsertPoint(
      key,
      (current) => ({
        ...current,
        baseRatePercent: current.baseRatePercent ?? livePoint.baseRatePercent,
        createdAt: livePoint.createdAt,
        dateLabel: livePoint.dateLabel,
        estimatedPaymentMonthly: livePoint.estimatedPaymentMonthly,
        llpaPoints: livePoint.llpaPoints,
        marketObservationDate:
          livePoint.marketObservationDate ?? current.marketObservationDate,
        personalizedRatePercent: livePoint.personalizedRatePercent,
        pricingSourceAsOf:
          livePoint.pricingSourceAsOf ?? current.pricingSourceAsOf,
        pricingSourceName:
          livePoint.pricingSourceName ?? current.pricingSourceName,
        source: "live",
        trigger:
          current.source === "stored" ? current.trigger : livePoint.trigger,
      }),
      livePoint,
    );
  }

  return Array.from(pointsByDay.values()).sort(
    (left, right) =>
      (parseOptionalDate(left.createdAt)?.getTime() ?? 0) -
      (parseOptionalDate(right.createdAt)?.getTime() ?? 0),
  );
}

function rateWatchDateKey(value: string | null) {
  const parsed = parseOptionalDate(value ?? undefined);
  if (!parsed) {
    return value ?? "missing-date";
  }

  return parsed.toISOString().slice(0, 10);
}

function resolvePreviousPersonalizedPoint(
  history: DashboardRateWatchHistoryPoint[],
  livePoint: DashboardRateWatchHistoryPoint | null,
) {
  if (history.length === 0) {
    return null;
  }

  const latest = history.at(-1) ?? null;
  if (!latest) {
    return null;
  }

  if (
    livePoint &&
    latest.personalizedRatePercent === livePoint.personalizedRatePercent &&
    rateWatchDateKey(latest.createdAt) === rateWatchDateKey(livePoint.createdAt)
  ) {
    return history.length > 1 ? history[history.length - 2] : null;
  }

  return latest;
}

function buildPreviewRateWatchHistory(): DashboardRateWatchHistoryPoint[] {
  return previewRateWatchHistorySeed.map((point) => ({
    ...point,
    dateLabel: formatMonthDayLabel(point.createdAt, false) ?? "Saved point",
    source: "preview",
  }));
}

function buildLiveRateWatchHistoryPoint(
  currentQuote: FinancialsRateWatchCurrentQuote | null,
): DashboardRateWatchHistoryPoint | null {
  if (!currentQuote) {
    return null;
  }

  const createdAt =
    currentQuote.pricing_source_as_of ?? currentQuote.base_rate_as_of ?? null;
  const hasRenderablePoint =
    typeof currentQuote.base_rate_percent === "number" ||
    typeof currentQuote.personalized_rate_percent === "number" ||
    typeof currentQuote.llpa_points === "number" ||
    typeof currentQuote.estimated_payment_monthly === "number";

  if (!createdAt || !hasRenderablePoint) {
    return null;
  }

  return buildLiveRateWatchHistoryPointFromValues({
    baseRatePercent: currentQuote.base_rate_percent ?? null,
    createdAt,
    estimatedPaymentMonthly: currentQuote.estimated_payment_monthly ?? null,
    llpaPoints: currentQuote.llpa_points ?? null,
    marketObservationDate: currentQuote.base_rate_as_of ?? null,
    personalizedRatePercent: currentQuote.personalized_rate_percent ?? null,
    pricingSourceAsOf: currentQuote.pricing_source_as_of ?? null,
    pricingSourceName: currentQuote.pricing_source_name ?? null,
  });
}

function buildLiveRateWatchHistoryPointFromValues({
  baseRatePercent,
  createdAt,
  estimatedPaymentMonthly,
  llpaPoints,
  marketObservationDate,
  personalizedRatePercent,
  pricingSourceAsOf,
  pricingSourceName,
}: {
  baseRatePercent: number | null;
  createdAt: string | null;
  estimatedPaymentMonthly: number | null;
  llpaPoints: number | null;
  marketObservationDate: string | null;
  personalizedRatePercent: number | null;
  pricingSourceAsOf: string | null;
  pricingSourceName: string | null;
}): DashboardRateWatchHistoryPoint | null {
  const hasRenderablePoint =
    typeof baseRatePercent === "number" ||
    typeof personalizedRatePercent === "number" ||
    typeof llpaPoints === "number" ||
    typeof estimatedPaymentMonthly === "number";

  if (!createdAt || !hasRenderablePoint) {
    return null;
  }

  return {
    baseRatePercent,
    createdAt,
    dateLabel: formatMonthDayLabel(createdAt, false) ?? "Current point",
    estimatedPaymentMonthly,
    llpaPoints,
    marketObservationDate,
    personalizedRatePercent,
    pricingSourceAsOf,
    pricingSourceName,
    source: "live",
    trigger: "initial",
  };
}

function buildLiveRateWatchVisualizationHistory(
  livePoint: DashboardRateWatchHistoryPoint,
): DashboardRateWatchHistoryPoint[] {
  if (!import.meta.env.DEV) {
    return [livePoint];
  }

  return [
    buildSyntheticRatePoint(livePoint, {
      baseRateDelta: 0.001,
      dayOffset: 14,
      estimatedPaymentDelta: 135,
      llpaDelta: 0.00125,
      personalizedRateDelta: 0.0015,
      trigger: "initial",
    }),
    buildSyntheticRatePoint(livePoint, {
      baseRateDelta: 0.00025,
      dayOffset: 7,
      estimatedPaymentDelta: 65,
      llpaDelta: 0.00125,
      personalizedRateDelta: 0.00075,
      trigger: "rate_change",
    }),
    livePoint,
  ];
}

function buildSyntheticRatePoint(
  livePoint: DashboardRateWatchHistoryPoint,
  config: {
    baseRateDelta: number;
    dayOffset: number;
    estimatedPaymentDelta: number;
    llpaDelta: number;
    personalizedRateDelta: number;
    trigger: DashboardRateWatchHistoryPoint["trigger"];
  },
): DashboardRateWatchHistoryPoint {
  const createdAt = shiftIsoDate(livePoint.createdAt, -config.dayOffset);
  const pricingSourceAsOf = shiftIsoDate(
    livePoint.pricingSourceAsOf ?? livePoint.createdAt,
    -config.dayOffset,
  );
  const marketObservationDate = shiftIsoDate(
    livePoint.marketObservationDate ?? livePoint.createdAt,
    -config.dayOffset,
  );

  return {
    baseRatePercent:
      typeof livePoint.baseRatePercent === "number"
        ? livePoint.baseRatePercent + config.baseRateDelta
        : null,
    createdAt,
    dateLabel: formatMonthDayLabel(createdAt, false) ?? "Saved point",
    estimatedPaymentMonthly:
      typeof livePoint.estimatedPaymentMonthly === "number"
        ? livePoint.estimatedPaymentMonthly + config.estimatedPaymentDelta
        : null,
    llpaPoints:
      typeof livePoint.llpaPoints === "number"
        ? Math.max(0, livePoint.llpaPoints + config.llpaDelta)
        : null,
    marketObservationDate,
    personalizedRatePercent:
      typeof livePoint.personalizedRatePercent === "number"
        ? livePoint.personalizedRatePercent + config.personalizedRateDelta
        : null,
    pricingSourceAsOf,
    pricingSourceName: livePoint.pricingSourceName,
    source: "synthetic",
    trigger: config.trigger,
  };
}

function shiftIsoDate(value: string, days: number) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

function buildEngineRates(
  raw: BackendEngineRates | null | undefined,
): EngineRates | null {
  if (!raw) return null;
  return {
    baseRatePercent: raw.base_rate_percent ?? null,
    effectivePropertyTaxRatePercent:
      raw.effective_property_tax_rate_percent ?? null,
    effectiveInsuranceRatePercent: raw.effective_insurance_rate_percent ?? null,
    monthlyMortgageInsuranceCents:
      raw.monthly_mortgage_insurance_cents ?? null,
  };
}

function buildAffordabilityScenarios(
  scenarios: AffordabilityScenario[] | null,
): DashboardAffordabilityScenario[] {
  if (!scenarios?.length) {
    return [];
  }

  return scenarios.map((scenario) => ({
    assumptions: scenario.assumptions ?? [],
    confidence: scenario.confidence ?? null,
    customerSummary: scenario.customer_summary ?? null,
    downPaymentNeeded: scenario.down_payment_needed,
    downPaymentPercent: scenario.down_payment_percent,
    hasPMI: scenario.has_pmi,
    label: scenario.label,
    maxDTIRatio: scenario.max_dti_ratio,
    maxPurchasePrice: scenario.max_purchase_price,
    missingInputs: scenario.missing_inputs ?? [],
    monthlyPayment: scenario.monthly_payment,
    status: scenario.status ?? "ready",
  }));
}

function buildRateSummary(rateWatch: RateWatchSnapshot) {
  if (rateWatch.status !== "ready" && rateWatch.status !== "preview") {
    return "Rate Watch will populate after Sill has a live pricing run for your current profile.";
  }
  if (typeof rateWatch.baseRatePercent === "number") {
    return `Base rate is ${formatPercent(rateWatch.baseRatePercent, 3)}. Pricing adjustment shows how your loan profile changes cost, and a lender quote decides how that cost lands in rate, points, or credits.`;
  }

  return "Base rate shows the product pricing starting point. Pricing adjustment shows how your loan profile changes cost, and a lender quote decides how that cost lands.";
}

function baseLLPAPointsForCreditBucket(creditBucket: string | null) {
  switch (creditBucket) {
    case "excellent_740+":
      return 0.00375;
    case "good_700_739":
      return 0.00875;
    case "fair_660_699":
      return 0.015;
    case "below_660":
      return 0.02125;
    default:
      return 0.01;
  }
}

function formatSignedPercentage(value: number | null, digits: number) {
  if (typeof value !== "number") {
    return "--";
  }

  const sign = value > 0 ? "+" : "";
  return `${sign}${formatPercentageNumber(value, digits)}%`;
}

function formatPercent(value: number, digits: number) {
  return `${formatPercentageNumber(value, digits)}%`;
}

function formatPercentageNumber(value: number, digits: number) {
  return trimTrailingZeros((value * 100).toFixed(digits));
}

export function trimTrailingZeros(value: string) {
  return value.replace(/\.?0+$/, "");
}

export function resolveRateWatchTrend(rateWatch: RateWatchSnapshot) {
  if (
    typeof rateWatch.personalizedRatePercent === "number" &&
    typeof rateWatch.previousPersonalizedRatePercent === "number"
  ) {
    return (
      rateWatch.personalizedRatePercent -
      rateWatch.previousPersonalizedRatePercent
    );
  }

  return rateWatch.weeklyDeltaPercent;
}

function formatMonthDayLabel(
  value: string | null,
  allowPreviewFallback: boolean,
) {
  if (!value) {
    return allowPreviewFallback ? "Apr 17" : null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) {
    return allowPreviewFallback ? "Apr 17" : null;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(parsed);
}
